import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import _ from 'lodash';

import { LoanApplicationTabRouteParam } from 'constants/loanApplication';

import { selectEntityReferenceCandidatesByType } from 'store/loanApplications/selectors';

import useEnsureEntityReferenceData from '../../../hooks/useEnsureEntityReferenceData';
import {
  getEntityQueryAtCaret,
  getEntityReferenceKey,
  groupEntityReferenceCandidates,
} from '../../../TeamChat.entityReferences.utils';
import { getApplicationIdFromChannelId } from '../../../TeamChat.utils';

import { FINANCIAL_SUBTAB_TO_ENTITY_TYPE } from './constants';

// Entity references work on BOTH of a loan's channels — internal AND the broker-visible TPO chat
// (DEV-5697: the TPO chat belongs to the broker's own loan, so every referenced entity is data the
// broker already sees in their portal). getApplicationIdFromChannelId returns null for non-loan
// channels (DMs etc.), so gating on the resolved applicationId still excludes those.
const useChatEntityReferences = ({ chatId }) => {
  const applicationId = getApplicationIdFromChannelId(chatId);
  const candidatesByType = useSelector(selectEntityReferenceCandidatesByType);
  const { isLoadedForApplication } = useEnsureEntityReferenceData(applicationId);
  const { tab, subtab } = useParams();

  const [entityRange, setEntityRange] = useState(null);
  const [entityQuery, setEntityQuery] = useState('');
  const [focusedEntityKey, setFocusedEntityKey] = useState(null);
  const dismissedStartRef = useRef(null);

  const priorityType =
    tab === LoanApplicationTabRouteParam.FINANCIAL_INFO
      ? (FINANCIAL_SUBTAB_TO_ENTITY_TYPE[subtab] ?? null)
      : null;

  // Candidates render only once the financialInfo slice holds THIS chat's loan — a stale slice
  // from a previously-opened loan must never offer another loan's entities in this channel.
  const groups = useMemo(() => {
    if (_.isNil(entityRange) || !isLoadedForApplication) {
      return [];
    }

    return groupEntityReferenceCandidates({ candidatesByType, query: entityQuery, priorityType });
  }, [candidatesByType, entityQuery, entityRange, isLoadedForApplication, priorityType]);

  const flatItems = useMemo(() => groups.flatMap(group => group.items), [groups]);

  const prevFlatItemsRef = useRef(flatItems);

  if (prevFlatItemsRef.current !== flatItems) {
    prevFlatItemsRef.current = flatItems;

    if (!flatItems.length && !_.isNil(focusedEntityKey)) {
      setFocusedEntityKey(null);
    }

    if (flatItems.length) {
      const stillVisible = flatItems.some(item => getEntityReferenceKey(item) === focusedEntityKey);

      if (!stillVisible) {
        setFocusedEntityKey(getEntityReferenceKey(flatItems[0]));
      }
    }
  }

  const updateFromCaret = useCallback(
    (value, caret, { isSuppressed = false } = {}) => {
      if (isSuppressed || !applicationId) {
        setEntityRange(null);
        setEntityQuery('');

        return;
      }

      const range = getEntityQueryAtCaret(value, caret);

      // Without the latch, Esc-dismiss is instantly undone: the next keyup re-detects the same
      // `#query` at the caret and re-opens the popup. The latch holds until the caret leaves the
      // trigger range; a NEW `#` (different start) re-opens normally.
      if (!range) {
        dismissedStartRef.current = null;
      }

      if (range && dismissedStartRef.current === range.start) {
        setEntityRange(null);
        setEntityQuery('');

        return;
      }

      setEntityRange(range);
      setEntityQuery(range?.query ?? '');
    },
    [applicationId],
  );

  const reset = useCallback(() => {
    dismissedStartRef.current = null;
    setEntityRange(null);
    setEntityQuery('');
  }, []);

  const dismiss = useCallback(() => {
    setEntityRange(previous => {
      dismissedStartRef.current = previous?.start ?? null;

      return null;
    });
    setEntityQuery('');
  }, []);

  // A `#` query is open on a real loan chat but the financial-info slice hasn't yet loaded THIS loan
  // — the popup renders a "Loading loan items…" row instead of a misleading "No matching items"
  // (DEV-5657). Non-loan channels never set a range (applicationId null), so they stay silent.
  const isLoading = !_.isNil(entityRange) && !!applicationId && !isLoadedForApplication;

  return {
    entityRange,
    entityQuery,
    groups,
    flatItems,
    focusedEntityKey,
    setFocusedEntityKey,
    updateFromCaret,
    reset,
    dismiss,
    isLoading,
    isCandidatesLoaded: isLoadedForApplication,
    candidatesByType,
  };
};

export default useChatEntityReferences;
